import React from 'react';
import Icon from '@mdi/react';
import { mdiOpenInNew } from '@mdi/js';
import { Tooltip } from '@enter/core';
import { formatDate } from '../../../services/primitives/date-utils';
import { formatMoney } from '../utils/statement-format.utils';
import { StatementDataTableColumn } from './statement-data-table';
import type { ClaimsTableRow } from '../statement-details.types';
import * as S from '../statement-details.styles';
import {
    StatementAmount,
    StatementMutedText as StatementMuted,
    StatementPrimaryText as StatementPrimary,
    StatementSecondaryText as StatementSecondary,
} from './statement-ui';

export type CreateClaimsTableColumnsArgs = {
    onGoToClaim?: (row: ClaimsTableRow) => void;
};

export const createClaimsTableColumns = ({
    onGoToClaim,
}: CreateClaimsTableColumnsArgs = {}): StatementDataTableColumn<ClaimsTableRow>[] => [
    {
        id: 'claim',
        header: 'Claim',
        width: 160,
        render: row => (
            <>
                <StatementPrimary>#{row.num}</StatementPrimary>
                <StatementSecondary>{row.statusLabel}</StatementSecondary>
            </>
        ),
    },
    {
        id: 'payer',
        header: 'Payer',
        width: 180,
        render: row => <StatementMuted>{row.payer}</StatementMuted>,
    },
    {
        id: 'charge',
        header: 'Charge',
        width: 110,
        render: row => <StatementAmount tone="strong">{formatMoney(row.charge)}</StatementAmount>,
    },
    {
        id: 'insurance',
        header: 'Insurance',
        width: 110,
        render: row => <StatementAmount>{formatMoney(row.insurance)}</StatementAmount>,
    },
    {
        id: 'adjustment',
        header: 'Adjustment',
        width: 110,
        render: row => (
            <StatementAmount tone="muted">{formatMoney(row.adjustment, { negative: true })}</StatementAmount>
        ),
    },
    {
        id: 'patientResp',
        header: 'Patient resp.',
        width: 120,
        render: row => (
            <StatementAmount tone={row.patientResp > 0 ? 'emphasis' : 'muted'}>
                {formatMoney(row.patientResp)}
            </StatementAmount>
        ),
    },
    {
        id: 'maxPatientResp',
        header: 'Max PR',
        width: 110,
        render: row =>
            row.maxPatientResp === undefined ? (
                <StatementMuted>—</StatementMuted>
            ) : (
                <StatementAmount tone={row.maxPatientResp > 0 ? 'emphasis' : 'muted'}>
                    {formatMoney(row.maxPatientResp)}
                </StatementAmount>
            ),
    },
    {
        id: 'dos',
        header: 'DOS',
        width: 120,
        render: row => (
            <StatementMuted>{row.dos ? formatDate(row.dos, 'MMM dd, yyyy') : '—'}</StatementMuted>
        ),
    },
    {
        id: 'goToClaim',
        header: '',
        width: 56,
        align: 'right',
        render: row => {
            if (!row.href || !onGoToClaim) return null;
            return (
                <S.PaymentActions>
                    <Tooltip direction="top" title={<span>Go to claim</span>}>
                        <span>
                            <S.PaymentActionButton
                                type="button"
                                variant="unstyled"
                                aria-label="Go to claim"
                                onClick={event => {
                                    event.stopPropagation();
                                    onGoToClaim(row);
                                }}>
                                <Icon path={mdiOpenInNew} size={0.75} />
                            </S.PaymentActionButton>
                        </span>
                    </Tooltip>
                </S.PaymentActions>
            );
        },
    },
];
